import { useEffect, useState, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import api from '@/lib/api';

export default function MaterialAutocomplete({ value, onChange, onSelect, autoFocus }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [items, setItems] = useState([]);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const load = useCallback(async (q) => {
    try {
      const r = await api.get('/materials', { params: { search: q || undefined } });
      setItems(r.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (open) load(query);
  }, [open, query, load]);

  const filtered = items;
  const exact = filtered.find((m) => m.name.toLowerCase() === (query || '').toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            data-testid="pengeluaran-material-autocomplete"
            autoFocus={autoFocus}
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); onChange?.(e.target.value); if (!open) setOpen(true); }}
            placeholder="Ketik nama material..."
            className="pr-8"
          />
          <ChevronsUpDown className="w-3.5 h-3.5 text-[var(--fpccs-text-subtle)] absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command shouldFilter={false} className="bg-transparent">
          <CommandList>
            {filtered.length === 0 && query && (
              <CommandEmpty className="py-2 px-3 text-xs">
                <div className="text-muted-fpccs">Tidak ditemukan.</div>
              </CommandEmpty>
            )}
            {filtered.length > 0 && (
              <CommandGroup heading="Master Material">
                {filtered.slice(0, 30).map((m) => (
                  <CommandItem
                    key={m.id}
                    value={m.name}
                    onSelect={() => {
                      onChange?.(m.name);
                      onSelect?.(m);
                      setQuery(m.name);
                      setOpen(false);
                    }}
                    data-testid={`material-option-${m.id}`}
                    className="flex items-center justify-between"
                  >
                    <div className="flex flex-col">
                      <span>{m.name}</span>
                      <span className="text-[10px] text-subtle-fpccs">{m.category} • {m.default_unit}</span>
                    </div>
                    {value?.toLowerCase() === m.name.toLowerCase() && <Check className="w-3.5 h-3.5 text-[var(--fpccs-primary)]" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {query && !exact && (
              <CommandGroup heading="Baru">
                <CommandItem
                  data-testid="pengeluaran-material-create-new"
                  value={`new-${query}`}
                  onSelect={() => {
                    onChange?.(query);
                    onSelect?.({ __isNew: true, name: query });
                    setOpen(false);
                  }}
                  className="text-[var(--fpccs-primary)]"
                >
                  <Plus className="w-3.5 h-3.5 mr-2" /> Gunakan "{query}" (bisa disimpan ke master setelah transaksi)
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
